import {Availability, BookingRestriction, Experience, FlexOffer, FlexOfferPrice, MediaImage} from "./types";
import {mapExperience} from "./Reducers/ExperienceReducer";
import {DefaultApi} from "@bookingkit/api-v4";
import InvalidApiV4ResponseError from "./Errors/InvalidApiV4ResponseError";
import {fromApiAvailability} from "./Reducers/AvailabilityReducer";
import {format} from "date-fns";
import languages from "../../Widgets/ModalWidget/Composables/Constants/Languages";
import {fromApiExperienceMedia} from "./Reducers/ExperienceMediaReducer";
import {fromApiToExperienceCalendarDay} from "./Reducers/ExperienceCalendarDayReducer";
import {ExperienceCalendarDay} from "./Models/ExperienceCalendarDay";
import {mapFlexOffers} from "./Reducers/FlexOfferReducer";
import {mapFlexOfferPrices} from "./Reducers/FlexOfferPricesReducer";

type CancellableRequest = {
    signal ?: AbortSignal
}

export type GetAvailabilitiesInput = {
    experienceId: string,
    startAt: Date,
    optionId?: string,
    asc?:1 | 0 | boolean,
    pageToken?: string,
}

export type GetAvailabilitiesResult = {
    availabilities: Availability[],
    pageToken?: string,
}
export class InventoryService {
    private readonly connection: DefaultApi;
    private readonly widgetId :string
    constructor(widgetId:string, connection:DefaultApi) {
        this.widgetId = widgetId;
        this.connection =  connection;
    };


    async getAvailabilities(getAvailabilitiesInput: GetAvailabilitiesInput, param?: CancellableRequest): Promise<GetAvailabilitiesResult> {
        const startAt = format(getAvailabilitiesInput.startAt, "yyyy-MM-dd'T'HH:mm:ss.SSSXXX");
        const { data } = await this.connection.listWidgetExperiencesAvailabilities({
            widgetId: this.widgetId,
            experienceId: getAvailabilitiesInput.experienceId,
            localStartTime: startAt,
            _options: getAvailabilitiesInput.optionId,
            pageSize: 50,
            asc: getAvailabilitiesInput.asc as boolean,
            pageToken: getAvailabilitiesInput.pageToken,
        }, {signal: param?.signal})
        if(!data?.data) {
            throw InvalidApiV4ResponseError.fromMissingProperty('data', 'object', data);
        }
        return {
            availabilities: data.data.map(fromApiAvailability),
            pageToken: data.metadata?.['page-token'],
        };

    }

    async getExperiences(experienceIds?:string,
                         pageSize:number = 25,
                         previewToken?: string|false):Promise<Experience[]> {
        const supportedLanguages = languages.map((language) => language.value);
        const headers: {authorization?: string} = {}
        if(previewToken){
            headers.authorization = previewToken
        }
        const { data } = await this.connection.listWidgetExperiences({
            widgetId: this.widgetId,
            experienceIds: experienceIds,
            locales: supportedLanguages.toString(),
            pageSize: pageSize
        }, {
            headers
        });
        if(!data?.data) {
            throw InvalidApiV4ResponseError.fromMissingProperty('data', 'object', data);
        }
        return data?.data?.map(mapExperience) ?? [];
    }

    async getFlexOffers(pageSize:number = 25,
                        previewToken?: string|false):Promise<FlexOffer[]> {
        const headers: {authorization?: string} = {};
        if(previewToken){
            headers.authorization = previewToken
        }
        const { data } = await this.connection.widgetFlexOfferList({
            widgetId: this.widgetId,
            pageSize: pageSize,
        }, {
            headers
        });
        if(!data?.data) {
            throw InvalidApiV4ResponseError.fromMissingProperty('data', 'object', data);
        }
        return data?.data?.map(mapFlexOffers) ?? [];
    }

    async getExperienceMedia(experienceId?: string): Promise<MediaImage[]> {
        const {data} = await this.connection.listWidgetExperienceMedia({
            widgetId: this.widgetId,
            experienceId: experienceId as string
        });
        if (!data?.data) {
            throw InvalidApiV4ResponseError.fromMissingProperty('data', 'object', data);
        }
        return  data?.data?.map(fromApiExperienceMedia)
            .sort((a:MediaImage, b:MediaImage) => (a.sort ?? 0 )- (b?.sort ?? 0));

    }

    async getExperienceCalendar(experienceId: string,
                                restriction: BookingRestriction,
                                startAt: Date,
                                endAt?: Date,
                                param?: CancellableRequest,
                                ): Promise<ExperienceCalendarDay[]> {
        const startAtFormatted = format(startAt, "yyyy-MM-dd");
        const endAtFormatted = endAt ? format(endAt, "yyyy-MM-dd") : undefined;
        const { data } = await this.connection.getWidgetExperienceCalendar({
            widgetId: this.widgetId,
            experienceId: experienceId,
            localStartDate: startAtFormatted,
            localEndDate: endAtFormatted
        }, {signal: param?.signal});
        if (!data?.data) {
            throw InvalidApiV4ResponseError.fromMissingProperty('data', 'object', data);
        }
        return data.data.map((day) => fromApiToExperienceCalendarDay(day, restriction));
    }
    async getFlexOfferPrices(flexOfferId:string,
                        previewToken?: string|false, pageSize:number = 50):Promise<FlexOfferPrice[]> {
        const headers: {authorization?: string} = {};
        if(previewToken){
            headers.authorization = previewToken
        }
        const { data } = await this.connection.listWidgetFlexOfferPrices({
            widgetId: this.widgetId,
            flexOfferId,
            pageSize,
        }, {
            headers
        });
        if(!data?.data) {
            throw InvalidApiV4ResponseError.fromMissingProperty('data', 'object', data);
        }
        return data?.data?.map(mapFlexOfferPrices) ?? [];
    }

}

export default { ExperienceService: InventoryService };